import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { ApiKey, RequestOptions } from '@api/abstracts/models';
import { CACHE, CacheInput, IS_CACHE_ENABLED } from '@api/interceptors';
import { environment } from '@environments/environment';
import { ContextService } from '@services/context';
import { ElasticSearchQueryInput } from '@services/elastic-search/models';
import { LogsService } from '@services/logs';
import { LogLevel } from '@services/logs/enums';
import { ApiEndpoints, CustomError, Integrations } from '@shared/models';
import { normalizeUrlPathSlashes } from '@shared/utils';
import { EMPTY, Observable, timeout, TimeoutError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

export enum RequestMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

interface RequestInterface {
  method: RequestMethod;
  url: string;
}

type LoggerPartialBody = Partial<{ body?: unknown }>;

export abstract class ApiServiceBase<T> {
  readonly #http: HttpClient = inject(HttpClient);
  readonly #contextService = inject(ContextService);
  readonly #logsService = inject(LogsService);

  readonly #url!: string;
  readonly #apiKey?: ApiKey;

  protected constructor(
    protected endpoint: string,
    protected api: keyof ApiEndpoints = 'default',
    protected integrationApiKey?: keyof Integrations
  ) {
    this.#url = environment.API.endpoint[api];
    this.#apiKey = integrationApiKey
      ? this.#parseApiKey(environment.integrations?.[integrationApiKey]?.apiKey || '')
      : undefined;
  }

  /**
   * Builds and returns the request options object, including caching and authentication details
   * if applicable.
   *
   * @param {CacheInput} [cache] - The cache configuration to be used for the request, if any.
   * @param {ApiKey} [apiKey] - An optional API key object. If the type is 'Bearer', it will
   *                            be combined into the authorization options.
   */
  static buildRequestOptions(cache?: CacheInput, apiKey?: ApiKey): Record<string, unknown> {
    let options = this.getCacheOptions(cache);

    // Check if apiKey is a Bearer token
    if (apiKey && apiKey.type === 'Bearer') {
      const token = apiKey.value;

      // Merge auth options with existing options
      options = {
        ...options,
        ...this.#getAuthOptions(token),
      };
    }

    return options;
  }

  /**
   * Returns options related to the authentication header (Bearer token).
   * @param token Bearer token to inject into the HTTP request
   */
  static #getAuthOptions(token?: string): {
    headers?: HttpHeaders;
  } {
    // If no token is provided, return an empty object
    if (!token) {
      return {};
    }

    // Adds the Bearer token to the authorization header
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return { headers };
  }

  /**
   * Retourne les options concernant le cache (context)
   * @param cache Paramètres de mise en cache des données
   */
  private static getCacheOptions(cache?: CacheInput): { context?: HttpContext } {
    const context = new HttpContext();
    if (cache) {
      context.set(IS_CACHE_ENABLED, environment.isCacheEnabled);
      context.set(CACHE, { cacheExpirationTime: cache.cacheExpirationTime, forcedKey: cache.forcedKey });
    }
    return { context };
  }

  public getRoot(cache?: CacheInput): Observable<T> {
    const options = ApiServiceBase.getCacheOptions(cache);
    const url = `${this.#url}/${this.endpoint}`;
    return this.#http.get<T>(url, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      })
    );
  }

  public get(id: string, queryOptions: Record<string, string> = {}, cache?: CacheInput): Observable<T> {
    const options = ApiServiceBase.buildRequestOptions(cache, this.#apiKey);
    const queryParams = this.objectToQuerystring(queryOptions);
    const url = `${this.#url}/${this.endpoint}/${id}${queryParams}`;
    return this.#http.get<T>(url, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      })
    );
  }

  public getMultiple(queryOptions: Record<string, string> = {}, cache?: CacheInput): Observable<T[]> {
    const options = ApiServiceBase.buildRequestOptions(cache, this.#apiKey);
    const queryParams = this.objectToQuerystring(queryOptions);
    const url = `${this.#url}/${this.endpoint}${queryParams}`;
    return this.#http.get<T[]>(url, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      })
    );
  }

  public getSlug(
    slugOverride: string | null = null,
    queryOptions: Record<string, string> = {},
    cache?: CacheInput
  ): Observable<T> {
    const options = ApiServiceBase.getCacheOptions(cache);
    const queryParams = this.objectToQuerystring(queryOptions);
    if (slugOverride !== null) {
      this.endpoint = slugOverride;
    }
    const url = `${this.#url}/slug?slug=${this.endpoint}${queryParams}`;
    return this.#http.get<T>(url, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      })
    );
  }

  readPrefetch<T>(jsonRoot: string, cache?: CacheInput): Observable<T> {
    const options = ApiServiceBase.getCacheOptions(cache);
    const url = normalizeUrlPathSlashes(`/prefetch/${jsonRoot}.json`);
    const fallbackUrl = normalizeUrlPathSlashes(`/prefetch/404.json`);
    this.#logsService.debug('ApiServiceBase', 'readPrefetch', { url, fallbackUrl });
    return this.#http.get<T>(url, { ...options, responseType: 'json' }).pipe(
      timeout(5000),
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      }),
      catchError((error: unknown) => {
        const isTimeout = error instanceof TimeoutError;
        const status = (error as CustomError).status;
        const context = { url, fallbackUrl, error, isTimeout, status };
        if (status === 404 || status === 200) {
          // unknown slug (404, or SPA-fallback HTML served as 200): expected bot traffic, keep quiet in prod
          this.#logsService.warn('ApiServiceBase', 'readPrefetch error', context);
        } else {
          // timeout or unexpected failure: page resolution breaks with zero trace otherwise (logLevel ERROR in prod)
          this.#logsService.error('ApiServiceBase', 'readPrefetch error', context);
        }
        if (isTimeout || status === 200 || status === 404) {
          return this.#http.get<T>(fallbackUrl, { ...options, responseType: 'json' }).pipe(
            tap((response) => {
              this.logger({ method: RequestMethod.GET, url: fallbackUrl }, response);
            })
          );
        }
        return EMPTY;
      })
    );
  }

  readElastic<T>(payload: ElasticSearchQueryInput, cache?: CacheInput): Observable<T> {
    const options = ApiServiceBase.buildRequestOptions(cache, this.#apiKey);
    const url = `${this.#url}/${this.endpoint}`;
    return this.#http.post<T>(url, payload, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.POST, url }, response, payload);
      })
    );
  }

  public create(item: Omit<T, 'id'>, includeFournierNetwork = false): Observable<T> {
    const options = ApiServiceBase.buildRequestOptions(undefined, this.#apiKey);
    const url = `${this.#url}/${this.endpoint}`;

    const fournierNetwork = environment.integrations?.fournierWebservice?.network;
    const body = includeFournierNetwork && fournierNetwork ? { ...item, network: fournierNetwork } : item;

    return this.#http.post<T>(url, body, options).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.POST, url }, response, item);
      })
    );
  }

  /**
   * Requête à l'API
   * @param method Method (GET, POST, PUT, DELETE, PATCH ...)
   * @param endpoint Url de la requête
   * @param options Options à passer avec la requête
   * @param params Paramètres (ex : si external = true, signifie que la requête doit taper sur une url externe)
   * @param cache Paramètres de mise en cache des données
   * @param useLegacyOptionsBuilder If true, uses the legacy options builder method instead of the new buildRequestOptions
   */
  protected customRequest<R>(
    method: RequestMethod,
    endpoint: string,
    options?: object,
    cache?: CacheInput,
    params = {
      external: false,
    },
    useLegacyOptionsBuilder = true
  ): Observable<R> {
    // If url is not external, build the url
    const url = params.external ? endpoint : `${this.#url}/${endpoint}`;

    // Build requests options. (headers, body, params, auth)
    if (useLegacyOptionsBuilder) {
      options = this.prepareRequestOptions(options!);
      options = cache ? { ...options, ...ApiServiceBase.getCacheOptions(cache) } : options;
    } else {
      options = options ?? ApiServiceBase.buildRequestOptions(cache, this.#apiKey);
    }

    return this.#http.request<R>(method, url, options).pipe(
      tap((response) => {
        this.logger({ method, url }, response, options);
      })
    );
  }

  /**
   * Affiche un log en console sur l'environnement local
   * @param req Paramètre de la requête
   * @param response Réponse de la requête
   * @param body Corps de la requête
   */
  private logger(req: RequestInterface, response: unknown, body: LoggerPartialBody = {}): void {
    if (environment.isLocal && this.#contextService.isBrowser()) {
      this.#logsService.groupCollapsed(LogLevel.DEBUG, 'ApiServiceBase', `${req.method.toUpperCase()} ${req.url}`);
      if ([RequestMethod.POST, RequestMethod.PUT, RequestMethod.PATCH].includes(req.method)) {
        const bodyItem = body?.body ? body.body : body;
        this.#logsService.debug('', 'Body', bodyItem);
      }
      this.#logsService.debug('', 'Response', response);
      this.#logsService.groupEnd(LogLevel.DEBUG);
    }
  }

  /**
   * Transforme un objet en query string
   * @param obj objet
   */
  private objectToQuerystring(obj: Record<string, string>): string {
    if (this.#apiKey && this.#apiKey.type !== 'Bearer' && this.#apiKey.value) {
      obj = {
        ...obj,
        ...this.#apiKey,
      };
    }
    if (!obj) {
      return '';
    }
    return Object.keys(obj).reduce((str, key, i) => {
      const delimiter = i === 0 ? '?' : '&';
      key = encodeURIComponent(key);
      let val;
      if (typeof obj[key] === 'object') {
        val = encodeURIComponent(JSON.stringify(obj[key]));
      } else {
        val = encodeURIComponent(obj[key]!);
      }
      return val === 'undefined' ? str : [str, delimiter, key, '=', val].join('');
    }, '');
  }

  /**
   * Parses a raw API key string formatted as <type>key.
   * Returns an object with the extracted type and key if valid, otherwise undefined.
   *
   * @param rawKey - The raw key string to parse.
   * @returns An object with 'type' and 'key' properties or undefined if format is invalid.
   */
  #parseApiKey(rawKey: string): ApiKey | undefined {
    const regex = /^<(?<type>[^>]+)>(?<value>.+)$/;
    const match = rawKey.match(regex);
    if (!match?.groups) return { type: 'api_key', value: rawKey };

    const { type, value } = match.groups;

    // Ensure neither part is empty (extra validation)
    if (!type || !type.trim() || !value || !value.trim()) return undefined;
    return { type, value };
  }

  /**
   * Ajout des options de requête (headers, body, params…)
   * @param options Options de la requête
   */
  private prepareRequestOptions(options: RequestOptions): RequestOptions {
    const requestOptions: RequestOptions = {
      headers: this.buildHeaders({ ...environment.API.options.headers, ...options.headers }),
      params: options.params || {},
      responseType: options.responseType || 'json',
      withCredentials: true,
    };

    if (options.body && Object.keys(options.body).length > 0) {
      requestOptions.body = options.body;
    }

    return requestOptions;
  }

  /**
   * Construction du header de la requête
   * @param headers Headers
   */
  private buildHeaders(headers: Record<string, string>): HttpHeaders {
    if (headers) {
      let h = new HttpHeaders();
      Object.keys(headers).forEach((optHeaders) => {
        h = h.set(optHeaders, headers[optHeaders]!);
      });
      return h;
    } else {
      return new HttpHeaders();
    }
  }
}
